import { SingleSelectComboboxField } from '@/components/form-components/SingleSelectCombobox'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useCurrentOrganisation } from '../useCurrentOrganisation'
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { restErrorMessage } from '../OrganisationTokenLimitsPage/restErrorMessage'
import { type TokenLimitOverrideRow } from '../OrganisationTokenLimitsPage/TokenLimitOverrideRow'
import clsx from 'clsx'

type OrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']

const formSchema = z.object({
  userId: z.number({ required_error: 'Select a user' }).positive('Select a user'),
  limitUsd: z.coerce.number({ invalid_type_error: 'Enter a daily limit in USD' }).min(0, 'Must be zero or more'),
})

type TUserTokenLimitForm = z.infer<typeof formSchema>

type UserTokenLimitFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  selectedOverride?: TokenLimitOverrideRow
  organisationUsers: OrganisationUser[]
  existingOverrideUserIds: number[]
}

export function UserTokenLimitForm(props: UserTokenLimitFormProps) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const edit = !!props.selectedOverride
  const { id: orgId } = useCurrentOrganisation()
  const {
    getSubscriptionsApiGetOrganisationTokenLimits,
    putSubscriptionsApiCreateUserTokenLimit,
    postSubscriptionsApiUpdateUserTokenLimit,
  } = useRestClient()

  const invalidate = () =>
    getSubscriptionsApiGetOrganisationTokenLimits.invalidateQueries({
      parameters: { path: { orgId } },
    })

  const { mutate: createOverride, isPending: isCreating } = putSubscriptionsApiCreateUserTokenLimit.useMutation(
    undefined,
    {
      onSuccess: () => {
        invalidate()
        setSheetOpen(false)
        toast.success('Token limit override added')
        props.onSubmit?.()
      },
      onError: (error) => toast.error(restErrorMessage(error, 'Failed to add token limit override')),
    }
  )

  const { mutate: updateOverride, isPending: isUpdating } = postSubscriptionsApiUpdateUserTokenLimit.useMutation(
    undefined,
    {
      onSuccess: () => {
        invalidate()
        setSheetOpen(false)
        toast.success('Token limit override updated')
        props.onSubmit?.()
      },
      onError: (error) => toast.error(restErrorMessage(error, 'Failed to update token limit override')),
    }
  )

  const isPending = isCreating || isUpdating

  const submitHandler = useCallback(
    (data: TUserTokenLimitForm) => {
      if (edit) {
        updateOverride({
          path: { orgId, userId: data.userId },
          body: { limitUsd: data.limitUsd },
        })
        return
      }

      createOverride({
        path: { orgId, userId: data.userId },
        body: { limitUsd: data.limitUsd },
      })
    },
    [createOverride, edit, orgId, updateOverride]
  )

  return (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
      wide
    >
      <FormFields
        edit={edit}
        selectedOverride={props.selectedOverride}
        organisationUsers={props.organisationUsers}
        existingOverrideUserIds={props.existingOverrideUserIds}
        isPending={isPending}
        sheetOpen={sheetOpen}
        onSubmit={submitHandler}
      />
    </SheetWithFormComponent>
  )
}

function FormFields({
  edit,
  selectedOverride,
  organisationUsers,
  existingOverrideUserIds,
  isPending,
  sheetOpen,
  onSubmit,
}: {
  edit: boolean
  selectedOverride?: TokenLimitOverrideRow
  organisationUsers: OrganisationUser[]
  existingOverrideUserIds: number[]
  isPending: boolean
  sheetOpen: boolean
  onSubmit: (data: TUserTokenLimitForm) => void
}) {
  const excludedUserIds = useMemo(() => new Set(existingOverrideUserIds), [existingOverrideUserIds])

  const userOptions = useMemo(
    () =>
      organisationUsers
        .filter((user) => (edit ? user.id === selectedOverride?.userId : !excludedUserIds.has(user.id)))
        .map((user) => ({
          value: user.id,
          label: clsx(user.givenName, user.familyName),
          description: user.email,
        })),
    [edit, excludedUserIds, organisationUsers, selectedOverride?.userId]
  )

  const form = useForm<TUserTokenLimitForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      userId: selectedOverride?.userId ?? 0,
      limitUsd: selectedOverride?.limitUsd,
    },
  })

  useEffect(() => {
    if (!sheetOpen) return
    form.reset({
      userId: selectedOverride?.userId ?? 0,
      limitUsd: selectedOverride?.limitUsd,
    })
  }, [form, selectedOverride, sheetOpen])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {edit ? (
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">User</span>
            <span className="text-base">
              {clsx(selectedOverride?.givenName, selectedOverride?.familyName)}
            </span>
            <span className="text-xs text-muted-foreground">{selectedOverride?.email}</span>
          </div>
        ) : (
          <SingleSelectComboboxField
            control={form.control}
            name="userId"
            label="User"
            options={userOptions}
            placeholder="Select a user"
            searchPlaceholder="Search users..."
            emptyMessage="No users available for an override."
            allowClear={false}
          />
        )}
        <FormField
          control={form.control}
          name="limitUsd"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Daily limit (USD)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="0.00"
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(event.target.value === '' ? undefined : event.target.value)}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              </FormControl>
              <FormDescription>Zero blocks daily spend for this user.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="self-end" disabled={isPending}>
          {edit ? 'Save' : 'Add override'}
        </Button>
      </form>
    </Form>
  )
}
